/**
 * AI 차시 놀이(PICNIC-MB-FIELD-001 v0.2 §4.7 · §6.12 · 2026-10-05) — AI01~AI36 을 차시마다 다른 놀이로.
 *
 * 카드(가상 카드 · 진짜 카메라)를 보이면 클래스 이름이 보드로 간다(§6.6). 놀이터는 **카드에 반응한 보드**를 보고 놀이를 진행한다 —
 * 카드를 보인 뒤 REACT_MS 안에 보드가 반응했는가(화면이 바뀜 · 켜짐 · 음 · 라디오 · 숫자)를 놀이 종류마다 다르게 본다.
 * 놀이 종류 여덟: 주문(requests) · 퀴즈(quiz) · 차례(sequence) · 보였다 치우기(toggle) · 판(rounds) · 주사위(dice) · 음(tones) · 짝에게(radio).
 * 카드 이름은 아이 프로그램이 기다리는 이름(Bench 가 알려 준다)이 먼저 — 없으면 차시 스타터의 이름(Bench contracts/lessons.microbit.json).
 * 순수 상태(DOM · 시계 없음) — 지금 시각 ms 를 받는다.
 */
import type { MbFrame } from "./live";
import { isBlank } from "./live";
import { bitsOfLeds, glyphOf } from "./font";

export type QuestKind = "requests" | "quiz" | "sequence" | "toggle" | "rounds" | "dice" | "tones" | "radio";

export interface Quest {
  readonly id: string;
  readonly emoji: string;
  readonly kind: QuestKind;
  /** 목표 수(주사위는 칸 수 · 퀴즈는 맞혀야 할 수) */
  readonly goal: number;
  /** 차시 스타터의 카드 이름(아이 프로그램 이름이 없을 때 진열대) */
  readonly cards: readonly string[];
  /** 차례(sequence) — 이 이름 순서 · 퀴즈(quiz) — 문제마다 정답 카드(이름) */
  readonly order?: readonly string[];
  /** 판(rounds)을 서로 다른 카드로만 센다 · 카드마다 n 번씩 */
  readonly distinct?: boolean;
  readonly perCard?: number;
  /**
   * 추울 때만(AI29 창문 관리인 — 카메라 + 온도 AND): 이 카드(order 첫 카드)는 온도가 below° 아래일 때만 반응으로 센다 — 따뜻하면 '아직 따뜻해요'.
   * 온도를 모르면(진짜 보드가 센서를 보내지 않음) 예전처럼 센다. 놀이터가 시뮬레이터에 온도를 청한다(MicrobitScreen)
   */
  readonly cold?: { readonly below: number };
}

const q = (id: string, emoji: string, kind: QuestKind, goal: number, cards: string[], extra: Partial<Quest> = {}): Quest =>
  ({ id, emoji, kind, goal, cards, ...extra });

/** 36차시(§4.7 표 — 놀이 · 미션) */
export const QUESTS: readonly Quest[] = [
  q("AI01", "🍎", "requests", 3, ["사과", "바나나"]),
  q("AI02", "😊", "requests", 3, ["웃는 카드", "우는 카드", "화난 카드"]),
  q("AI03", "⚽", "toggle", 3, ["공", "빈손"]),
  q("AI04", "🚪", "requests", 3, ["빨강 카드", "파랑 카드", "노랑 카드"]),
  q("AI05", "🎲", "dice", 10, ["눈 1", "눈 2", "눈 3"]),
  q("AI06", "🎤", "tones", 2, ["하트 카드", "별 카드"]),
  q("AI07", "🧸", "toggle", 3, ["손 인형", "빈손"]),
  q("AI08", "🌗", "sequence", 3, ["해 그림", "달 그림"], { order: ["해 그림", "달 그림", "해 그림"] }),
  q("AI09", "🧪", "requests", 2, ["불 그림", "얼음 그림"]),
  q("AI10", "📮", "radio", 2, ["하트 카드", "별 카드"], { distinct: true }),
  q("AI11", "🪨", "radio", 3, ["숫자 1", "숫자 2", "숫자 3"]),
  q("AI12", "🖼️", "rounds", 2, ["내 물건 1", "내 물건 2"], { distinct: true }),
  q("AI13", "🎭", "requests", 3, ["웃음 카드", "슬픔 카드", "졸림 카드"]),
  q("AI14", "♻️", "rounds", 9, ["캔", "병", "종이"], { perCard: 3 }),
  q("AI15", "✊", "rounds", 3, ["가위", "바위", "보"]),
  q("AI16", "🔔", "quiz", 4, ["O 카드", "X 카드"], { order: ["O 카드", "X 카드", "O 카드", "O 카드", "X 카드"] }),
  q("AI17", "🎲", "dice", 15, ["눈 1", "눈 2", "눈 3", "눈 4", "눈 5", "눈 6"]),
  q("AI18", "👣", "toggle", 10, ["발 카드", "빈손"]),
  q("AI19", "🌙", "toggle", 3, ["해 카드", "달 카드"]),
  q("AI20", "🌦️", "requests", 2, ["여름 카드", "겨울 카드"]),
  q("AI21", "🎵", "tones", 3, ["도 카드", "미 카드", "솔 카드"]),
  q("AI22", "💡", "toggle", 3, ["인형", "빈손"]),
  q("AI23", "🚚", "radio", 2, ["사과", "바나나"], { distinct: true }),
  q("AI24", "📚", "requests", 3, ["물병", "책", "빈손"]),
  q("AI25", "🧩", "radio", 3, ["동그라미", "세모", "네모"], { distinct: true }),
  q("AI26", "🚗", "radio", 3, ["앞으로", "뒤로", "정지"], { distinct: true }),
  q("AI27", "🗳️", "radio", 2, ["O 카드", "X 카드"], { distinct: true }),
  q("AI28", "📔", "rounds", 5, ["맑음", "흐림", "비"]),
  q("AI29", "🪟", "sequence", 2, ["창문 열림", "창문 닫힘"], { order: ["창문 열림", "창문 닫힘"], cold: { below: 18 } }),
  q("AI30", "🛏️", "sequence", 3, ["침대", "책상", "문"], { order: ["침대", "책상", "문"] }),
  q("AI31", "📡", "radio", 3, ["맑음", "흐림", "비"], { distinct: true }),
  q("AI32", "🏛️", "radio", 1, ["평소", "낯선 물건"]),
  q("AI33", "🔍", "toggle", 2, ["사과", "바나나"]),
  q("AI34", "🔐", "sequence", 3, ["빨강", "파랑", "노랑"], { order: ["빨강", "파랑", "노랑"] }),
  q("AI35", "🗺️", "toggle", 1, ["보물"]),
  q("AI36", "⏱️", "rounds", 2, ["책", "공", "빈손"], { distinct: true }),
];

export const questOf = (lesson: string | null | undefined): Quest | null => {
  const t = (lesson ?? "").trim().replace(/^MB-/i, "").toUpperCase();
  return QUESTS.find((x) => x.id === t) ?? null;
};

/** 카드를 보인 뒤 보드 반응을 기다리는 시간 */
export const REACT_MS = 2500;

export interface QuestState {
  readonly count: number;
  /** 퀴즈 — 답한 문제 수 */
  readonly answered: number;
  readonly wrong: number;
  /** 판(rounds) — 카드마다 센 수 · 서로 다른 카드 */
  readonly perCard: Readonly<Record<string, number>>;
  /** 음(tones) — 반응한 카드 · 들린 음(Hz) */
  readonly tonesBy: Readonly<Record<string, number>>;
  /** 보였다 치우기 — 한 번 센 뒤 보드가 꺼져야 다시 센다 */
  readonly armed: boolean;
  readonly pending: { readonly card: string; readonly at: number; readonly bits: string; readonly blank: boolean; readonly tn: number; readonly sent: number } | null;
  /** 마지막 일(화면 문구) — ok: 반응을 셌다 · other: 지금 차례가 아닌 카드 · quiet: 보드가 반응하지 않았다 */
  readonly last: { readonly card: string; readonly result: "ok" | "other" | "quiet" | "wrong" | "warm" } | null;
}
export const initialQuest = (): QuestState => ({ count: 0, answered: 0, wrong: 0, perCard: {}, tonesBy: {}, armed: false, pending: null, last: null });

/** 지금 쓸 카드 — 아이 프로그램의 이름이 먼저 */
export const questCards = (quest: Quest, programClasses: readonly string[]): readonly string[] => (programClasses.length ? programClasses : quest.cards);

/** 차례 · 퀴즈의 이름 — 아이 프로그램에 그 이름이 없으면 프로그램 카드 순서로 바꿔 쓴다 */
function orderOf(quest: Quest, cards: readonly string[]): readonly string[] {
  const order = quest.order ?? [];
  if (order.length && order.every((n) => cards.includes(n))) return order;
  if (quest.kind === "quiz") {
    const o = cards.find((c) => /^o\b|o 카드|^o$/i.test(c)) ?? cards[0];
    const x = cards.find((c) => /^x\b|x 카드|^x$/i.test(c)) ?? cards[1] ?? cards[0];
    return (quest.order ?? []).map((n) => (/^x/i.test(n) ? x! : o!));
  }
  return quest.kind === "sequence" ? cards.slice(0, quest.goal) : cards;
}

/** 지금 놀이터가 바라는 카드(주문 · 퀴즈 · 차례) — 없으면 null(아무 카드나) */
export function wantedCard(quest: Quest, cards: readonly string[], s: QuestState): string | null {
  if (!cards.length) return null;
  if (quest.kind === "requests") {
    // 진열대를 차례로(같은 카드가 두 번 이어지지 않게) — '빈손'은 주문하지 않는다(카드가 둘 이상일 때)
    const pool = cards.filter((c) => !/빈손/.test(c));
    const list = pool.length ? pool : cards;
    return list[s.count % list.length]!;
  }
  if (quest.kind === "quiz") return orderOf(quest, cards)[s.answered] ?? null;
  if (quest.kind === "sequence") return orderOf(quest, cards)[s.count] ?? null;
  return null;
}

export const questGoal = (quest: Quest, cards: readonly string[]): number => {
  if (quest.kind === "sequence") return orderOf(quest, cards).length;
  if (quest.perCard) return quest.perCard * cards.filter((c) => !/빈손/.test(c)).length;
  if (quest.distinct) return Math.min(quest.goal, cards.filter((c) => !/빈손/.test(c)).length || 1);
  return quest.goal;
};
export const quizTotal = (quest: Quest) => quest.order?.length ?? 0;

export function questDone(quest: Quest, cards: readonly string[], s: QuestState): boolean {
  if (quest.kind === "quiz") return s.count >= quest.goal;
  return s.count >= questGoal(quest, cards);
}

/** 카드를 보였다(가상 카드를 눌렀거나 카메라가 새로 알아봤다) */
export function questCard(quest: Quest, cards: readonly string[], s: QuestState, card: string, nowMs: number, frame: MbFrame | null, radioSent: number): QuestState {
  if (questDone(quest, cards, s)) return s;
  // 퀴즈를 다 풀었는데 모자라면 처음부터(골든벨 다시)
  if (quest.kind === "quiz" && s.answered >= quizTotal(quest)) s = { ...initialQuest(), wrong: s.wrong };
  const want = wantedCard(quest, cards, s);
  if (quest.kind === "requests" && want !== null && card !== want) return { ...s, last: { card, result: "other" } };
  if (quest.kind === "sequence" && want !== null && card !== want) return { ...s, count: 0, wrong: s.wrong + 1, pending: null, last: { card, result: "wrong" } };
  // 추울 때만(AI29) — 첫 카드(창문 열림)는 온도가 기준 아래일 때만 센다. 따뜻하면 차례는 그대로 두고 알려 준다(온도를 모르면 예전처럼)
  const temp = frame?.sensors?.temp;
  if (quest.cold && card === orderOf(quest, cards)[0] && temp !== undefined && temp >= quest.cold.below) return { ...s, pending: null, last: { card, result: "warm" } };
  if (quest.kind === "quiz" && want !== null && card !== want) {
    return { ...s, answered: s.answered + 1, wrong: s.wrong + 1, pending: null, last: { card, result: "wrong" } };
  }
  const leds = frame?.leds ?? [];
  return {
    ...s,
    pending: { card, at: nowMs, bits: frame ? bitsOfLeds(leds) : "", blank: !frame || isBlank(leds), tn: frame?.tone?.n ?? 0, sent: radioSent },
  };
}

/** 시계 한 걸음 — 기다리던 반응이 왔나 */
export function questTick(quest: Quest, cards: readonly string[], s: QuestState, nowMs: number, frame: MbFrame | null, radioSent: number): QuestState {
  let st = s;
  if (quest.kind === "toggle" && st.armed && frame && isBlank(frame.leds)) st = { ...st, armed: false };
  const p = st.pending;
  if (!p || !frame) return st;
  if (nowMs - p.at > REACT_MS) return { ...st, pending: null, last: { card: p.card, result: "quiet" } };
  const bits = bitsOfLeds(frame.leds);
  const lit = !isBlank(frame.leds);
  const tone = frame.tone && frame.tone.n !== p.tn && frame.tone.hz > 20 ? frame.tone.hz : null;
  let reacted: boolean;
  switch (quest.kind) {
    case "toggle": reacted = lit && !st.armed && (p.blank || bits !== p.bits); break;   // 꺼져 있다가 켜짐(이미 켜져 있던 것은 아니다)
    case "tones": reacted = tone !== null; break;
    case "radio": reacted = radioSent > p.sent; break;
    case "dice": reacted = lit && (bits !== p.bits || p.blank) && /^[1-9]$/.test(glyphOf(frame.leds) ?? ""); break;
    default: reacted = lit && (bits !== p.bits || p.blank);
  }
  if (!reacted) return st;
  const done = { ...st, pending: null, last: { card: p.card, result: "ok" as const } };
  switch (quest.kind) {
    case "toggle": return { ...done, count: st.count + 1, armed: true };
    case "dice": return { ...done, count: st.count + Number(glyphOf(frame.leds)) };
    case "tones": {
      const tonesBy = { ...st.tonesBy, [p.card]: tone! };
      return { ...done, tonesBy, count: new Set(Object.keys(tonesBy)).size };
    }
    case "quiz": return { ...done, count: st.count + 1, answered: st.answered + 1 };
    case "rounds":
    case "radio": {
      const perCard = { ...st.perCard, [p.card]: (st.perCard[p.card] ?? 0) + 1 };
      const count = quest.perCard
        ? Object.entries(perCard).filter(([c]) => !/빈손/.test(c)).reduce((n, [, v]) => n + Math.min(v, quest.perCard!), 0)
        : quest.distinct ? Object.keys(perCard).filter((c) => !/빈손/.test(c)).length : st.count + 1;
      return { ...done, perCard, count };
    }
    default: return { ...done, count: st.count + 1 };
  }
}
